import { Alert, Box, Button, Form, Header, Modal, Multiselect, Select, SelectProps, SpaceBetween, Table, TableProps } from '@amzn/awsui-components-core';
import { FieldArray, Formik, FormikHelpers, FormikProps } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useSelector } from 'react-redux';
import { useAuth } from 'src/app/auth/AuthContextProvider';
import { ErrorFallback } from 'src/components/common/ErrorFallback';
import { LoadingSpinner } from 'src/components/common/LoadingSpinner';
import { BUSINESS_GROUP_CODES } from 'src/constants/business-group-constants';
import { COST_CENTER_SEGMENT_NAME, PRODUCT_LINE_SEGMENT_NAME } from 'src/constants/corp-segment-constants';
import { eLocalStorageKeys, FINANCE_ADMIN, SELECT_ALL_OPTION, USER_TYPES } from 'src/constants/generic-constants';
import { Status } from 'src/models/AuthContextModels';
import { CorpSegmentAccess, CurrentUserAccessDetails, UserDataClassifications } from 'src/models/XptUsersModel';
import { selectFilteredCCAndPLOfAllBusinessGroups } from 'src/store/selectors/corpSegmentSelectors';
import { RootState } from 'src/store/store';
import { arrayOfStringsToSelectDropdownOptions, getMultiSelectPlaceHolderValue } from 'src/utils/generic-utilities';
import { dmmDataClassificationId } from '../business-group/businessGroupSelectors';

export interface ModifyUserAccessProps {
  showModal: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * One editable row in the access table: a business-group access entry paired with its index in the
 * Formik `user_data_classifications` array, which cell renderers use to target mutations
 * (setFieldValue / remove) since the Table cell API does not expose the item's index.
 */
type AccessRow = { dataClassification: UserDataClassifications; originalIndex: number };

export const ModifyUserAccess: React.FC<ModifyUserAccessProps> = ({ showModal, onCancel, onConfirm }) => {
  // Auth hooks and state management
  const { userAlias, updateUserAuthDetails, resetToDefaultAuthDetails } = useAuth();

  // Redux selectors for user access and business groups
  const { currentUserAccessDetails, currentUserAccessLoadingStatus } = useSelector((state: RootState) => state.xptAccessAndAuthorizationStore);
  const { businessGroups, businessGroupLoadingStatus } = useSelector((state: RootState) => state.businessGroupStore);
  const filteredCCAndPLOfAllBusinessGroups = useSelector(selectFilteredCCAndPLOfAllBusinessGroups);
  const dmmDataClassificationIdValue = useSelector(dmmDataClassificationId);

  // Local state management
  const [formInitializing, setFormInitializing] = useState<boolean>(true);
  const [userAccessForm, setUserAccessForm] = useState<CurrentUserAccessDetails>({} as CurrentUserAccessDetails);
  const userAccessFormRef = useRef<FormikProps<CurrentUserAccessDetails>>(null);
  const [isFormDirty, setIsFormDirty] = useState(false);

  // Reset form when modal is shown
  useEffect(() => {
    if (showModal) {
      userAccessFormRef?.current?.resetForm();
    }
  }, [showModal]);

  // Initialize form data when business groups are loaded and user access details are either loaded or failed (no access).
  useEffect(() => {
    const accessResolved = currentUserAccessLoadingStatus === Status.Completed || currentUserAccessLoadingStatus === Status.Failed;
    if (businessGroupLoadingStatus === Status.Completed && accessResolved) {
      setFormInitializing(true);

      // Filter active data classifications
      const activeDataClassificationsIDs = businessGroups
        .filter((bg) => bg.item_metadata.is_active)
        .map((dt) => dt.data_classification.data_classification_id!);

      const businessGroupNameOf = (dataClassificationId: number) =>
        businessGroups.find((bg) => bg.data_classification.data_classification_id === dataClassificationId)?.data_classification
          .data_classification_name ?? '';

      // Create user access object with filtered classifications.
      const currentUserAccess: CurrentUserAccessDetails = currentUserAccessDetails
        ? {
            ...currentUserAccessDetails,
            user_data_classifications: currentUserAccessDetails.user_data_classifications
              .filter((dt) => activeDataClassificationsIDs.includes(dt.data_classification_id))
              .sort((a, b) => businessGroupNameOf(a.data_classification_id).localeCompare(businessGroupNameOf(b.data_classification_id)))
          }
        : {
            user_alias: userAlias,
            user_access_modified: true,
            user_data_classifications: [] // access to no business groups, i.e. unauthorized state.
          };
      setUserAccessForm(currentUserAccess);

      setFormInitializing(false);
    }
  }, [businessGroups, businessGroupLoadingStatus, currentUserAccessLoadingStatus, currentUserAccessDetails, userAlias]);

  // Helper functions for business group data
  const getBusinessGroupName = (dataClassificationId: number) => {
    const businessGroupName =
      businessGroups.find((bg) => bg.data_classification.data_classification_id === dataClassificationId)?.data_classification
        .data_classification_name || '';
    return businessGroupName;
  };

  // Active business groups not already present in the form — the options for "Add Business Group".
  // Takes the current user permissions from the form, and filters them out from the overall business group list to
  // identify the remaining Business Groups without access.
  const getAddableBusinessGroupOptions = (userDataClassifications: UserDataClassifications[]): SelectProps.Option[] => {
    const currentDataClassificationIds = new Set(userDataClassifications.map((udc) => udc.data_classification_id));
    return businessGroups
      .filter((bg) => bg.item_metadata.is_active && !currentDataClassificationIds.has(bg.data_classification.data_classification_id!))
      .map((bg) => ({
        label: bg.data_classification.data_classification_name,
        value: String(bg.data_classification.data_classification_id)
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  // Create new UserDataClassifications with the user as the Finance Admin having access to all the segments within the business group
  const buildNewDataClassification = (dataClassificationId: number): UserDataClassifications => {
    const businessGroup = businessGroups.find((bg) => bg.data_classification.data_classification_id === dataClassificationId);
    const segmentOptions = filteredCCAndPLOfAllBusinessGroups.find((cc) => cc.dataClassificationId === dataClassificationId);

    // Default to granting ALL the CCs / PLs.
    const isDMM = businessGroup?.data_classification.data_classification_short_description === BUSINESS_GROUP_CODES.DMM;
    const defaultSegment: CorpSegmentAccess = isDMM
      ? { segment_name: PRODUCT_LINE_SEGMENT_NAME, segment_value: segmentOptions?.productLines ?? [] }
      : { segment_name: COST_CENTER_SEGMENT_NAME, segment_value: segmentOptions?.costCenters ?? [] };

    return {
      data_classification_id: dataClassificationId,
      data_classification_short_description: businessGroup?.data_classification.data_classification_short_description ?? '',
      user_type: FINANCE_ADMIN,
      corp_segment_access: [defaultSegment]
    };
  };

  // User type options helper
  const getUserTypes = () => {
    const userTypeOptions = USER_TYPES.map((userType) => ({ label: userType, value: userType }));
    return userTypeOptions;
  };

  // Cost center helpers
  const getCostCentersOfBusinessGroup = (dataClassificationId: number) => {
    const costCenterOptions: string[] =
      filteredCCAndPLOfAllBusinessGroups.find((cc) => cc.dataClassificationId === dataClassificationId)?.costCenters || [];
    return arrayOfStringsToSelectDropdownOptions(costCenterOptions);
  };

  const getSelectedCCOptions = (corpSegmentAccess: CorpSegmentAccess[]) => {
    const selectedCostCenters: string[] = corpSegmentAccess.find((cc) => cc.segment_name === COST_CENTER_SEGMENT_NAME)?.segment_value || [];
    return arrayOfStringsToSelectDropdownOptions(selectedCostCenters);
  };

  // Product line helpers
  const getProductLinesOfBusinessGroup = (dataClassificationId: number) => {
    const productLineOptions: string[] =
      filteredCCAndPLOfAllBusinessGroups.find((cc) => cc.dataClassificationId === dataClassificationId)?.productLines || [];
    return arrayOfStringsToSelectDropdownOptions(productLineOptions);
  };

  const getSelectedProductLineOptions = (corpSegmentAccess: CorpSegmentAccess[]) => {
    const selectedCostCenters: string[] = corpSegmentAccess.find((cc) => cc.segment_name === PRODUCT_LINE_SEGMENT_NAME)?.segment_value || [];
    return arrayOfStringsToSelectDropdownOptions(selectedCostCenters);
  };

  // Form action handlers
  const handleReset = () => {
    localStorage.removeItem(eLocalStorageKeys.UserAccessDetails);
    resetToDefaultAuthDetails();
    onCancel();
    window.location.reload();
  };

  const handleSubmit = async (formValues: CurrentUserAccessDetails, formikHelpers: FormikHelpers<CurrentUserAccessDetails>) => {
    try {
      const userDataClassifications = formValues.user_data_classifications.map((userDataClassification: UserDataClassifications) => {
        const { data_classification_short_description, data_classification_id, corp_segment_access } = userDataClassification;

        const isDMM = data_classification_short_description === BUSINESS_GROUP_CODES.DMM;

        const segmentOptions = filteredCCAndPLOfAllBusinessGroups.find((cc) => cc.dataClassificationId === data_classification_id);

        const newSegment = {
          segment_name: isDMM ? COST_CENTER_SEGMENT_NAME : PRODUCT_LINE_SEGMENT_NAME,
          segment_value: isDMM ? segmentOptions?.costCenters ?? [] : segmentOptions?.productLines ?? []
        };

        // Combine existing segments with the new one
        const updatedCorpSegAccess = [...corp_segment_access.filter((seg) => seg.segment_name !== newSegment.segment_name), newSegment];

        return {
          ...userDataClassification,
          corp_segment_access: updatedCorpSegAccess
        };
      });

      const userAccessDetailsToStoreInLocal: CurrentUserAccessDetails = {
        ...formValues,
        user_data_classifications: userDataClassifications,
        user_access_modified: true
      };

      // Store in local storage
      try {
        localStorage.setItem(eLocalStorageKeys.UserAccessDetails, JSON.stringify(userAccessDetailsToStoreInLocal));
      } catch (storageError) {
        console.error('Error storing data in localStorage:', storageError);
        throw new Error('Failed to save user access details');
      }

      // Update user auth details and reload
      await updateUserAuthDetails({ isDev: false }, true);
      onCancel();
      window.location.reload();
    } catch (error) {
      console.error('Error while processing user access details:', error);
      formikHelpers.setStatus({ error: 'Failed to update user access details' });
    }
  };

  const handleProductLineChange = (index: number, selectedOptions: SelectProps.Options) => {
    userAccessFormRef?.current?.setFieldValue(`user_data_classifications[${index}].corp_segment_access`, [
      {
        segment_name: PRODUCT_LINE_SEGMENT_NAME,
        segment_value: selectedOptions.map((option) => option.value)
      }
    ]);
  };

  return (
    <>
      <Modal
        data-testid="modify-auth-context"
        onDismiss={onCancel}
        visible={showModal}
        size="large"
        header={<Header description="You can modify access permissions to test different application behaviors.">{'Modify Your Access'}</Header>}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={onCancel}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => userAccessFormRef.current?.submitForm()} disabled={!isFormDirty}>
                Submit
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <div>
          {formInitializing && <LoadingSpinner />}
          {!formInitializing && (
            <ErrorBoundary
              FallbackComponent={ErrorFallback}
              onReset={() => {
                window.location.reload();
              }}
            >
              <Formik<CurrentUserAccessDetails>
                innerRef={userAccessFormRef}
                enableReinitialize={true}
                initialValues={userAccessForm}
                validateOnChange={true}
                validateOnBlur={true}
                onSubmit={handleSubmit}
              >
                {({ errors, handleBlur, handleSubmit, touched, dirty, isValid, setFieldTouched, values, setFieldValue, isSubmitting }) => {
                  // Update the dirty state whenever it changes
                  useEffect(() => {
                    setIsFormDirty(dirty);
                  }, [dirty]);

                  return (
                    <Form>
                      <SpaceBetween size="m" direction="vertical">
                        {values.user_access_modified && (
                          <Alert
                            type="info"
                            action={
                              <Button variant="normal" onClick={handleReset}>
                                Reset to Default
                              </Button>
                            }
                          >
                            {`You are currently viewing modified permission. Click "Reset to Default" to reset it.`}
                          </Alert>
                        )}
                        <FieldArray
                          name="user_data_classifications"
                          render={(arrayHelpers) => {
                            const items: AccessRow[] = (values.user_data_classifications ?? [])
                                .map((dataClassification, originalIndex) => ({
                                  dataClassification,
                                  originalIndex
                                }));

                            const addableOptions = getAddableBusinessGroupOptions(values.user_data_classifications);
                            const allBusinessGroupsAdded = addableOptions.length === 0;

                            const columnDefinitions: TableProps.ColumnDefinition<AccessRow>[] = [
                              {
                                id: 'business_group',
                                header: 'Business Group',
                                width: 150,
                                cell: ({ dataClassification }) => getBusinessGroupName(dataClassification.data_classification_id)
                              },
                              {
                                id: 'user_type',
                                header: 'User Type',
                                width: 150,
                                cell: ({ dataClassification, originalIndex }) => (
                                  <Select
                                    filteringType="auto"
                                    expandToViewport
                                    options={getUserTypes()}
                                    selectedOption={{ label: dataClassification.user_type, value: dataClassification.user_type }}
                                    onChange={({ detail }) => {
                                      setFieldValue(`user_data_classifications[${originalIndex}].user_type`, detail.selectedOption.value);
                                    }}
                                    onBlur={() => setFieldTouched(`user_data_classifications[${originalIndex}].user_type`, true)}
                                  />
                                )
                              },
                              {
                                id: 'segment',
                                header: 'Cost Center / Product Line',
                                width: 200,
                                cell: ({ dataClassification, originalIndex }) => {
                                  // DMM has Product Lines
                                  if (dmmDataClassificationIdValue === dataClassification.data_classification_id) {
                                    const productLineOptions = getProductLinesOfBusinessGroup(dataClassification.data_classification_id);
                                    const selectedProductLines = getSelectedProductLineOptions(dataClassification.corp_segment_access);
                                    const hasProductLines = productLineOptions.length > 0;
                                    return (
                                      <Multiselect
                                        expandToViewport
                                        hideTokens
                                        filteringType="auto"
                                        disabled={!hasProductLines}
                                        placeholder={
                                          hasProductLines
                                            ? getMultiSelectPlaceHolderValue(selectedProductLines, 'Product Line')
                                            : 'No product lines available'
                                        }
                                        options={[{ ...SELECT_ALL_OPTION, options: productLineOptions }]}
                                        selectedOptions={selectedProductLines}
                                        onChange={({ detail }) => handleProductLineChange(originalIndex, detail.selectedOptions)}
                                        onBlur={() => setFieldTouched(`user_data_classifications[${originalIndex}].corp_segment_access`, true)}
                                      />
                                    );
                                  }

                                  // Others have Cost Centers
                                  const costCenterOptions = getCostCentersOfBusinessGroup(dataClassification.data_classification_id);
                                  const selectedCostCenters = getSelectedCCOptions(dataClassification.corp_segment_access);
                                  const hasCostCenters = costCenterOptions.length > 0;
                                  return (
                                    <Multiselect
                                      expandToViewport
                                      hideTokens
                                      filteringType="auto"
                                      disabled={!hasCostCenters}
                                      placeholder={
                                        hasCostCenters
                                          ? getMultiSelectPlaceHolderValue(selectedCostCenters, 'Cost Center')
                                          : 'No cost centers available'
                                      }
                                      options={[{ ...SELECT_ALL_OPTION, options: costCenterOptions }]}
                                      selectedOptions={selectedCostCenters}
                                      onChange={({ detail }) => {
                                        setFieldValue(`user_data_classifications[${originalIndex}].corp_segment_access`, [
                                          { segment_name: COST_CENTER_SEGMENT_NAME, segment_value: detail.selectedOptions.map((option) => option.value) }
                                        ]);
                                      }}
                                      onBlur={() => setFieldTouched(`user_data_classifications[${originalIndex}].corp_segment_access`, true)}
                                    />
                                  );
                                }
                              },
                              {
                                id: 'remove',
                                header: '',
                                width: 56,
                                cell: ({ dataClassification, originalIndex }) => (
                                  <Button
                                    variant="inline-icon"
                                    iconName="remove"
                                    formAction="none"
                                    ariaLabel={`Remove ${getBusinessGroupName(dataClassification.data_classification_id)}`}
                                    onClick={() => arrayHelpers.remove(originalIndex)}
                                  />
                                )
                              }
                            ];

                            return (
                              <SpaceBetween size="m" direction="vertical">
                                <div className="modify-access-table">
                                  <Table
                                    variant="borderless"
                                    stickyHeader
                                    wrapLines
                                    items={items}
                                    trackBy={(row) => String(row.dataClassification.data_classification_id ?? row.originalIndex)}
                                    columnDefinitions={columnDefinitions}
                                    empty={
                                      <Alert type="warning">
                                        {`You don't have access to any business groups. Submitting now will simulate an unauthenticated user. Add a business group below to grant access instead.`}
                                      </Alert>
                                    }
                                  />
                                </div>

                                <div className="modify-access-bulk-actions">
                                  <SpaceBetween size="s" direction="horizontal" alignItems="center">
                                    <Box variant="awsui-key-label">Add Business Group</Box>
                                    <div className="width-30-rem">
                                      <Select
                                        data-testid="add-business-group"
                                        filteringType="auto"
                                        expandToViewport
                                        disabled={allBusinessGroupsAdded}
                                        placeholder={allBusinessGroupsAdded ? 'You have access to all business groups' : 'Select a business group to add'}
                                        selectedOption={null}
                                        options={addableOptions}
                                        onChange={({ detail }) => {
                                          const newId = Number(detail.selectedOption.value);
                                          arrayHelpers.push(buildNewDataClassification(newId));
                                        }}
                                      />
                                    </div>
                                  </SpaceBetween>
                                  <Button
                                    formAction="none"
                                    iconName="remove"
                                    disabled={items.length === 0}
                                    onClick={() => setFieldValue('user_data_classifications', [])}
                                  >
                                    Remove all
                                  </Button>
                                </div>
                              </SpaceBetween>
                            );
                          }}
                        ></FieldArray>
                      </SpaceBetween>
                    </Form>
                  );
                }}
              </Formik>
            </ErrorBoundary>
          )}
        </div>
      </Modal>
    </>
  );
};
